import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import {
  Loader2,
  Shield,
  AlertTriangle,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Activity,
  Lock,
  Unlock,
  Eye,
  Globe,
  Smartphone,
  Monitor
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function SecurityDashboard() {
  const { token } = useAuth();
  const [securityData, setSecurityData] = useState(null);
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSecurityData();
  }, []);

  const fetchSecurityData = async () => {
    try {
      setLoading(true);
      const [summaryRes, loginRes, sessionsRes] = await Promise.all([
        fetch(`${API}/api/admin/security/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/security/login-attempts`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/security/active-sessions`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSecurityData(data);
      }
      if (loginRes.ok) {
        const data = await loginRes.json();
        setLoginAttempts(data.attempts || []);
      }
      if (sessionsRes.ok) {
        const data = await sessionsRes.json();
        setActiveSessions(data.sessions || []);
      }
    } catch (error) {
      console.error("Error fetching security data:", error);
    } finally {
      setLoading(false);
    }
  };

  const invalidateSession = async (sessionId) => {
    try {
      const response = await fetch(`${API}/api/admin/security/invalidate-session/${sessionId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Session invalidated");
        fetchSecurityData();
      }
    } catch (error) {
      toast.error("Failed to invalidate session");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Security Dashboard</h2>
          <p className="text-slate-500">Monitor authentication and access patterns</p>
        </div>
        <Button variant="outline" onClick={fetchSecurityData}>
          <Activity className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Security Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-700">{securityData?.successful_logins_24h || 0}</p>
              <p className="text-sm text-emerald-600">Successful Logins (24h)</p>
            </div>
          </div>
        </div>
        <div className="bg-red-50 border border-red-100 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-700">{securityData?.failed_logins_24h || 0}</p>
              <p className="text-sm text-red-600">Failed Attempts (24h)</p>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{activeSessions.length}</p>
              <p className="text-sm text-blue-600">Active Sessions</p>
            </div>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{securityData?.suspicious_activities || 0}</p>
              <p className="text-sm text-amber-600">Suspicious Activities</p>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="sessions">
        <TabsList>
          <TabsTrigger value="sessions">
            <Users className="w-4 h-4 mr-2" />
            Active Sessions
          </TabsTrigger>
          <TabsTrigger value="attempts">
            <Clock className="w-4 h-4 mr-2" />
            Login History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sessions" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Device</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">IP Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Started</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {activeSessions.map(session => (
                  <tr key={session.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900">{session.user_name || session.email}</p>
                        <p className="text-xs text-slate-500">{session.role}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {session.device_type === 'mobile' ? (
                          <Smartphone className="w-4 h-4 text-slate-400" />
                        ) : (
                          <Monitor className="w-4 h-4 text-slate-400" />
                        )}
                        <span className="text-sm text-slate-600">{session.browser || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-slate-600">{session.ip_address || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Globe className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">{session.location || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {session.created_at ? new Date(session.created_at).toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => invalidateSession(session.id)}
                      >
                        <Lock className="w-4 h-4 mr-1" />
                        Revoke
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {activeSessions.length === 0 && (
              <div className="py-8 text-center text-slate-500">No active sessions</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="attempts" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">IP Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loginAttempts.map((attempt, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm">{attempt.email}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        attempt.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {attempt.success ? 'Success' : 'Failed'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-slate-600">{attempt.ip_address || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {attempt.timestamp ? new Date(attempt.timestamp).toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">{attempt.failure_reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {loginAttempts.length === 0 && (
              <div className="py-8 text-center text-slate-500">No login attempts recorded</div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
